import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import {
  createAssessment,
  deleteAssessment,
  updateAssessment,
  updateAssessmentOrder,
} from '@/data/services/assessmentServices';
import { Assessment } from '@/models/Assessment';
import Evaluation from '@/models/Evaluation';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { reorder } from '@/utils/list';

type UseAssessmentsProps = {
  evaluation: Evaluation;
  cacheManager: ListCacheManager<Evaluation>;
};

type UpdateMutationArgs = {
  assessmentId: number;
  data: Partial<Assessment>;
};

const useAssessments = ({ evaluation, cacheManager }: UseAssessmentsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const assessments = evaluation.assessments ?? [];

  const reorderMutation = useMutation({
    onMutate: (assessmentIds: number[]) => {
      const reorderedAssessments = reorder(assessments, assessmentIds, 'id');
      cacheManager.update({
        ...evaluation,
        assessments: reorderedAssessments,
      });
    },
    mutationFn: (assessmentIds: number[]) =>
      updateAssessmentOrder(evaluation.id, assessmentIds.map(id => ({ id }))),
    onSuccess: () => {
      alert.success(t('reorderAssessmentsSuccess'));
    },
    onError: err => {
      cacheManager.undo();
      alert.error(getErrorMessage(err));
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<Assessment>) =>
      createAssessment(evaluation.id, data),
    onSuccess: newAssessment => {
      cacheManager.update({
        ...evaluation,
        assessments: [...assessments, newAssessment],
      });
      alert.success(t('createAssessmentSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ assessmentId, data }: UpdateMutationArgs) =>
      updateAssessment(evaluation.id, assessmentId, data),
    onSuccess: updated => {
      cacheManager.update({
        ...evaluation,
        assessments: assessments.map(a => (a.id === updated.id ? updated : a)),
      });
      alert.success(t('updateAssessmentSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (assessmentId: number) =>
      deleteAssessment(evaluation.id, assessmentId),
    onSuccess: (_, assessmentId) => {
      cacheManager.update({
        ...evaluation,
        assessments: assessments.filter(a => a.id !== assessmentId),
      });
      alert.success(t('deleteAssessmentSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  return {
    assessments,
    cacheManager,
    reorderAssessments: reorderMutation.mutate,
    isReordering: reorderMutation.isPending,
    createAssessment: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateAssessment: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteAssessment: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};

export default useAssessments;
